import FormulaProcessor from './FormulaProcessor'
import utils from '@/helpers/utils'
import db from '@/services/db'
import store from '@/store'

class FormulaCaching {
  constructor (vm) {
    this.vm = vm
    this.sheets = {}
    this.sheetsWithFormulas = {}
    this.writeLocalSheets()
    this.read()
    this.parseAndCache()
    this.setMethod()
    return this.sheets
  }

  parseAndCache () {
    if (!utils.count(this.sheetsWithFormulas)) return

    let vm = this.vm
    let scope = {
      ...this.sheets
    }

    for (let key of vm.scopedData) {
      scope[key] = vm[key]
    }

    for (let sheetKey in this.sheetsWithFormulas) {
      let sheet = this.sheets[sheetKey]
      for (let cell in this.sheetsWithFormulas[sheetKey]) {
        if (!this.sheets[sheetKey][cell]) continue
        if (this.sheets[sheetKey][cell][0] !== '=') continue
        let val = sheet[cell]
        try {
          let processor = new FormulaProcessor(val, scope)
          val = processor.start()
        } catch (err) {
          val = '#ERROR: ' + err.message
        }
        this.sheets[sheetKey][cell] = val
      }
    }
  }

  writeLocalSheets () {
    const sheets = this.vm.spreadsheets

    if (!sheets) return

    for (const sheetKey in sheets) {
      const sheet = sheets[sheetKey]
      if (!sheet.isLocal) continue

      const data = {
        key: sheetKey,
        sheet: sheet
      }

      store.commit('setLocalSpreadsheets', data)
    }
  }

  read () {
    let sheets = this.vm.spreadsheets

    for (let sheetKey in sheets) {
      let sheet = sheets[sheetKey]
      if (sheetKey === '.key') continue
      if (sheet.isLocal) sheet = store.state.localSpreadsheets[sheetKey]

      if (!sheets[sheetKey].data) {
        this.sheets[sheetKey] = {}
      } else {
        this.sheets[sheetKey] = sheets[sheetKey].data
        const fmlsAdr = sheets[sheetKey].formulas

        if (fmlsAdr) {
          this.sheetsWithFormulas[sheetKey] = fmlsAdr
        }
      }
      this.sheets[sheetKey]['_sheet'] = sheetKey
      if (!sheet.subsheets) continue
      let subsheets = sheet.subsheets
      for (let subsheetKey in subsheets) {
        if (!subsheets[subsheetKey].data) {
          this.sheets[sheetKey + '.' + subsheetKey] = {}
        } else {
          this.sheets[sheetKey + '.' + subsheetKey] = subsheets[subsheetKey].data
          const fmlsAdr = sheets[sheetKey].subsheets[subsheetKey].formulas

          if (fmlsAdr) {
            this.sheetsWithFormulas[sheetKey + '.' + subsheetKey] = fmlsAdr
          }
        }
        this.sheets[sheetKey + '.' + subsheetKey]['_sheet'] = sheetKey + '.' + subsheetKey
      }
    }
  }

  getRange (start, finish, responseType) {
    let startCol = start.match(/[A-Z]+/)[0]
    let startRow = start.match(/\d+/)[0]
    let finishCol = finish.match(/[A-Z]+/g)[0]
    let finishRow = finish.match(/\d+/)[0]

    let result

    let coords = {
      startRow,
      finishRow,
      startCol,
      finishCol
    }

    if (startCol !== finishCol && startRow !== finishRow) {
      result = []
      for (let i = startRow; i <= finishRow; i++) {
        let startRow = i
        let coords = {
          startRow,
          startCol,
          finishCol
        }

        result.push(this.getHorizontalRange(coords, responseType))
      }
    } else if (startCol === finishCol) {
      result = this.getVerticalRange(coords, responseType)
    } else {
      result = this.getHorizontalRange(coords, responseType)
    }

    return result
  }

  getVerticalRange (coords, responseType) {
    let { startRow, finishRow, startCol } = coords

    let result
    if (typeof responseType === 'function' && responseType.name === 'Array') {
      result = []
      for (let i = startRow; i <= finishRow; i++) {
        let item = (this[startCol + i]) ? this[startCol + i] : null
        result.push(item)
      }
    } else {
      result = {}
      for (let i = startRow; i <= finishRow; i++) {
        let item = (this[startCol + i]) ? this[startCol + i] : null
        result[startCol + i] = item
      }
    }

    return result
  }

  getHorizontalRange (coords, responseType) {
    let { startRow, startCol, finishCol } = coords
    let start = utils.getColIndex(startCol)
    let finish = utils.getColIndex(finishCol)

    let result
    if (typeof responseType === 'function' && responseType.name === 'Array') {
      result = []
      for (let i = start; i <= finish; i++) {
        let cell = utils.getColLabel(i) + startRow
        let item = (this[cell]) ? this[cell] : null
        result.push(item)
      }
    } else {
      result = {}
      for (let i = start; i <= finish; i++) {
        let cell = utils.getColLabel(i) + startRow
        let item = (this[cell]) ? this[cell] : null
        result[cell] = item
      }
    }

    return result
  }

  setRange (start, verticalDirectionOrGivenData, givenData) {
    let direction = 'horizontal'
    let data = []
    let result = {}
    if (verticalDirectionOrGivenData === true) {
      direction = 'vertical'
      data = givenData
    } else {
      data = verticalDirectionOrGivenData
    }

    if (!data.length) throw new Error(`'setRange' method requires array of data, but no data provided`)

    let col = start.match(/[A-Z]+/)[0]
    let row = start.match(/\d+/)[0]

    if (direction === 'vertical') {
      for (let val of data) {
        result[col + row++] = val
      }
    } else {
      let startCol = utils.getColIndex(col)

      for (let val of data) {
        result[utils.getColLabel(startCol++) + row] = val
      }
    }

    db.sheets.update(this['_sheet'], result)
  }

  getData (responseType) {
    let result

    if (typeof responseType === 'function' && responseType.name === 'Array') {
      result = []

      for (let cell in this) {
        if (cell === '_sheet') continue
        let col = cell.match(/[A-Z]+/)[0]
        let rowIdx = cell.match(/\d+/)[0]

        let colIdx = utils.getColIndex(col)

        if (!result[rowIdx - 1]) result[rowIdx - 1] = []

        result[rowIdx - 1][colIdx - 1] = this[cell]
      }
    } else {
      result = {}

      for (let cell in this) {
        if (cell === '_sheet') continue

        result[cell] = this[cell]
      }
    }

    return result
  }

  setMethod () {
    for (let sheet in this.sheets) {
      let _this = this
      let methods = [
        'getData',
        'getRange',
        'getVerticalRange',
        'getHorizontalRange',
        'getColIndex',
        'setRange'
      ]

      for (let method of methods) {
        let options = {
          value: _this[method],
          enumerable: false,
          configurable: false
        }

        if (this.sheets[sheet].hasOwnProperty(method)) continue

        Object.defineProperty(this.sheets[sheet], method, options)
      }
    }
  }
}

export default FormulaCaching



// WEBPACK FOOTER //
// ./src/components/Spreadsheet/FormulaCaching.js